import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Button,
  Card,
  Label,
  Root,
  Select,
  SkeletonLoader,
  Spinner,
} from '@yoast/ui-library';
import cx from 'classnames';
import {
  useEffect,
  useState,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';

import {
  useGetAnalysisSummary,
  useGetBrands,
} from '../../api/generated/insights';
import {
  BrandStatusEnum,
  type AIProviderEnum,
  type AnalysisSummary,
} from '../../api/generated/insights.schemas';
import {
  setApiBaseUrl,
  setMyYoastAccessTokenFactory,
} from '../../orval/custom-instance';
import { getDeltaTextClassNames } from '../../utils/getDeltaTextClassNames';
import { getAIProviderLabel } from '../../utils/stringUtils';
import GradientText from '../GradientText';
import TooltipUncontrolled from '../TooltipUncontrolled';

export interface AiBrandInsightsSummaryProps {
  /**
   * Authentication token (MyYoast access token) used for API calls. Either a
   * plain string or a factory that returns the current token.
   */
  token: string | (() => string);

  /** Base URL of the AI Insights API (e.g. https://.../api/v1). */
  apiUrl?: string;

  /** Render in RTL mode. Defaults to false. */
  isRtl?: boolean;

  /** Optional CSS class for the root element. */
  className?: string;

  /** Optional inline styles for the root element. */
  style?: CSSProperties;

  /**
   * Called when the user wants to navigate into the full AI Brand Insights
   * tool. `to` is a path relative to the tool root (e.g. `/` or `/setup`).
   */
  onNavigate?: (to: string) => void;

  /**
   * Builds a real URL for a tool-relative path (e.g.
   * `hrefFor('/')` → `/ai-brand-insights-tool/`). When provided, navigation
   * call-to-action buttons render as anchors, so they behave like real links
   * (open in new tab, ⌘/middle-click, etc.). Falls back to `onNavigate` when
   * omitted.
   */
  hrefFor?: (to: string) => string;

  /**
   * Anchor `target` for the navigation call-to-action links (only applies when
   * `hrefFor` is set). Pass a named target (e.g. `'aiBrandInsights'`) to open —
   * and reuse — the tool in its own tab, matching how the host's navigation
   * opens it. Omit for same-tab navigation.
   */
  linkTarget?: string;
}

// Mirrors the QueryClient configuration used by the full app (src/App.tsx).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

// Brand gradient used for the title and primary call-to-action, matching the
// colours of the GradientText component.
const GRADIENT_STYLE: CSSProperties = {
  background: 'linear-gradient(102deg, #A61E69, #6366F1)',
};

/**
 * Renders a coloured delta (difference vs. the previous analysis). Higher is
 * better by default; pass `invert` for metrics where lower is better (ranking).
 */
const Delta: FC<{
  difference?: number;
  format?: 'absolute' | 'percentage';
  invert?: boolean;
}> = ({ difference, format = 'absolute', invert = false }) => {
  if (typeof difference !== 'number' || Number.isNaN(difference)) {
    return null;
  }
  const effective = invert ? difference * -1 : difference;
  return (
    <span
      className={cx(
        'yst-inline-flex yst-items-center yst-gap-0.5 yst-font-medium',
        getDeltaTextClassNames(effective),
      )}
    >
      {effective > 0 && <ChevronUpIcon className="yst-h-4 yst-w-4" />}
      {effective < 0 && <ChevronDownIcon className="yst-h-4 yst-w-4" />}
      {effective > 0 && '+'}
      {effective}
      {format === 'percentage' && '%'}
    </span>
  );
};

const MetricTile: FC<{
  label: string;
  tooltip: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  isLoading?: boolean;
  className?: string;
}> = ({ label, tooltip, value, delta, isLoading = false, className }) => (
  <div
    className={cx(
      'yst-relative yst-flex yst-flex-col yst-items-center yst-justify-center yst-gap-1 yst-p-6 yst-text-center',
      className,
    )}
  >
    <div className="yst-absolute yst-right-3 yst-top-3">
      <TooltipUncontrolled id={`aibi-summary-${label.replace(/\s+/g, '-').toLowerCase()}`}>
        {tooltip}
      </TooltipUncontrolled>
    </div>
    <div className="yst-text-3xl yst-font-bold yst-text-slate-900">
      {/* The invisible child gives the skeleton the exact line box of a real
          value, so tiles keep their height while loading. */}
      {isLoading ? <SkeletonLoader className="yst-w-14">0</SkeletonLoader> : value}
    </div>
    <div className="yst-text-slate-600">{label}</div>
    {/* Fixed-height slot so tiles keep the same height with or without a
        delta (and while loading), preventing layout shifts. */}
    <div className="yst-h-5">{!isLoading && delta}</div>
  </div>
);

const MetricsGrid: FC<{
  summary?: AnalysisSummary;
  isLoading?: boolean;
}> = ({ summary, isLoading = false }) => {
  // All four headline metrics come pre-computed from the lightweight summary
  // endpoint (mentions count, competitor ranking position, own-brand citation
  // count, standardized sentiment), each with its previous-scan delta.
  const mentions = summary?.mentions;
  const citations = summary?.citations;
  const ranking = summary?.ranking;
  const sentiment = summary?.sentiment;
  const rankingValue = ranking?.value;

  return (
    <div className="yst-grid yst-grid-cols-2 yst-rounded-lg yst-border yst-border-slate-200">
      <MetricTile
        className="yst-border-b yst-border-r yst-border-slate-200"
        label="Mentions"
        tooltip="The number of queries where your brand was mentioned."
        isLoading={isLoading}
        value={mentions?.value ?? '—'}
        delta={<Delta difference={mentions?.previous?.difference} />}
      />
      <MetricTile
        className="yst-border-b yst-border-slate-200"
        label="Competitor ranking"
        tooltip="Your brand's position versus competitors, based on mentions for your queries."
        isLoading={isLoading}
        value={
          typeof rankingValue === 'number' &&
          Number.isFinite(rankingValue) &&
          rankingValue > 0 ? (
            <span>
              #{rankingValue} {rankingValue === 1 && '🏆'}
            </span>
          ) : (
            '—'
          )
        }
        // Lower rank is better, so invert the delta.
        delta={<Delta difference={ranking?.previous?.difference} invert />}
      />
      <MetricTile
        className="yst-border-r yst-border-slate-200"
        label="Citations"
        tooltip="How often your brand's website is cited as a source in AI answers."
        isLoading={isLoading}
        value={citations?.value ?? '—'}
        delta={<Delta difference={citations?.previous?.difference} />}
      />
      <MetricTile
        label="Positive sentiment"
        tooltip="The share of mentions about your brand that are positive."
        isLoading={isLoading}
        value={
          sentiment?.value !== undefined && sentiment?.value !== null
            ? `${sentiment.value}%`
            : '—'
        }
        delta={
          <Delta
            difference={sentiment?.previous?.difference}
            format="percentage"
          />
        }
      />
    </div>
  );
};

/**
 * Loading placeholder that mirrors the loaded card's exact layout (title,
 * brand selector, metric grid, call-to-action), so the block does not reflow
 * between configuring → loading → loaded states.
 */
const SummarySkeleton: FC<
  Pick<AiBrandInsightsSummaryProps, 'onNavigate' | 'hrefFor' | 'linkTarget'>
> = ({ onNavigate = () => {}, hrefFor, linkTarget }) => {
  const visitCta = hrefFor
    ? {
        as: 'a' as const,
        href: hrefFor('/'),
        ...(linkTarget ? { target: linkTarget, rel: 'noopener noreferrer' } : {}),
      }
    : { onClick: () => onNavigate('/') };
  return (
    <Card className="yst-overflow-visible">
      <Card.Content className="yst-space-y-4">
        <GradientText as="h2" className="yst-text-xl yst-font-bold">
          AI Brand Insights
        </GradientText>
        <div className="yst-flex yst-flex-wrap yst-gap-4">
          <div className="yst-flex-1 yst-min-w-[200px] yst-space-y-2">
            <Label>Brand</Label>
            <SkeletonLoader className="yst-h-10 yst-w-full" />
          </div>
          <div className="yst-flex-1 yst-min-w-[200px] yst-space-y-2">
            <Label>AI platform</Label>
            <SkeletonLoader className="yst-h-10 yst-w-full" />
          </div>
        </div>
        <MetricsGrid isLoading />
        <div className="yst-flex yst-justify-end">
          <Button variant="primary" style={GRADIENT_STYLE} {...visitCta}>
            Visit AI Brand Insights
            <ArrowRightIcon className="yst-ml-2 yst-h-4 yst-w-4" />
          </Button>
        </div>
      </Card.Content>
    </Card>
  );
};

const SummaryContent: FC<
  Pick<AiBrandInsightsSummaryProps, 'onNavigate' | 'hrefFor' | 'linkTarget'>
> = ({ onNavigate = () => {}, hrefFor, linkTarget }) => {
  // A navigation CTA renders as a real anchor when a URL builder is available
  // (so it behaves like a link), and falls back to the onNavigate callback.
  // With a linkTarget it opens the tool in its own (reused) tab.
  const ctaProps = (to: string) =>
    hrefFor
      ? {
          as: 'a' as const,
          href: hrefFor(to),
          ...(linkTarget
            ? { target: linkTarget, rel: 'noopener noreferrer' }
            : {}),
        }
      : { onClick: () => onNavigate(to) };

  const {
    data: brands,
    isLoading: isLoadingBrands,
    isError: isBrandsError,
  } = useGetBrands({
    query: { select: ({ data }) => data?.brands },
  });

  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>();
  const [selectedProvider, setSelectedProvider] = useState<
    AIProviderEnum | undefined
  >();

  // Default the selection to the first brand once the list loads.
  useEffect(() => {
    if (!selectedBrandId && brands && brands.length > 0) {
      setSelectedBrandId(brands[0].id);
    }
  }, [brands, selectedBrandId]);

  // A single lightweight call returns the selected AI platform's headline
  // metrics for the latest completed analysis, plus the platforms that analysis
  // has scans for — replacing the previous list-then-detail waterfall against
  // the heavy getAnalysis endpoint.
  //
  // `selectedProvider` stays undefined until the user picks a platform, so the
  // first render sends no `aiProvider` and the endpoint answers with its own
  // default (the first completed scan). Defaulting the state from the response
  // instead would change the query key and cost a second request per brand.
  const { data: summaryData, isLoading: isLoadingSummary } =
    useGetAnalysisSummary(
      selectedBrandId ?? '',
      { aiProvider: selectedProvider },
      {
        query: {
          enabled: !!selectedBrandId,
          select: ({ data }) => data,
        },
      },
    );

  const summary = summaryData?.summary;
  // AI platforms with a completed scan in that analysis, server-ordered to match
  // the dashboard. Provider-less scans are already filtered out server-side, so
  // the selector never sees the empty-string value they would produce.
  const providers = summaryData?.providers ?? [];

  // Loading the initial brand list — show a skeleton matching the final layout.
  if (isLoadingBrands) {
    return (
      <SummarySkeleton
        onNavigate={onNavigate}
        hrefFor={hrefFor}
        linkTarget={linkTarget}
      />
    );
  }

  // No brand yet, or the user has no access (unlicensed → request errors).
  // A single enticing state invites them to get started in AIBI.
  if (isBrandsError || !brands || brands.length === 0) {
    return (
      <Card className="yst-overflow-visible">
        <Card.Content className="yst-space-y-4 yst-text-center">
          <GradientText as="h2" className="yst-text-xl yst-font-bold">
            AI Brand Insights
          </GradientText>
          <p className="yst-text-slate-600">
            See how your brand shows up in AI answers like ChatGPT, Gemini, Perplexity and Claude.
            Set up your first brand to start tracking your mentions,
            ranking, citations and sentiment.
          </p>
          <div>
            <Button
              variant="primary"
              style={GRADIENT_STYLE}
              {...ctaProps('/setup')}
            >
              Start your first analysis
              <ArrowRightIcon className="yst-ml-2 yst-h-4 yst-w-4" />
            </Button>
          </div>
        </Card.Content>
      </Card>
    );
  }

  // Before the default brand selection lands, the summary query is disabled and
  // reports not-loading; treat that render as loading too, so the block doesn't
  // flash the no-analysis message before the data arrives.
  const isLoadingMetrics = !selectedBrandId || isLoadingSummary;
  // The user has a brand but no completed analysis to show yet (summary is null).
  const hasNoCompletedAnalysis = !isLoadingMetrics && !summary;

  const selectedBrand = brands.find((brand) => brand.id === selectedBrandId);
  // Automatic analysis is disabled for this brand and no first analysis is
  // scheduled or in progress, so none is coming — don't promise one.
  const analysisDisabled =
    selectedBrand?.setupStatus === BrandStatusEnum.firstAnalysisNotScheduled;
  // The brand exists but the user never finished the setup wizard (no queries
  // chosen), so no analysis is scheduled or running — prompt them to finish
  // rather than promising an analysis that isn't coming.
  const setupIncomplete =
    selectedBrand?.setupStatus === BrandStatusEnum.pendingQuerySelection;

  const brandOptions = brands.map((brand) => ({
    value: brand.id,
    label: brand.name,
  }));

  // One option per AI platform present in the latest analysis. The fallback
  // keeps the disabled selector labelled while no scans are available.
  const providerOptions =
    providers.length > 0
      ? providers.map((provider) => ({
          value: provider as string,
          label: getAIProviderLabel(provider),
        }))
      : [{ value: '', label: 'AI platform' }];

  return (
    <Card className="yst-overflow-visible">
      <Card.Content className="yst-space-y-4">
        <GradientText as="h2" className="yst-text-xl yst-font-bold">
          AI Brand Insights
        </GradientText>

        {/* Brand + AI platform selectors */}
        <div className="yst-flex yst-flex-wrap yst-gap-4">
          <div className="yst-flex-1 yst-min-w-[200px] yst-space-y-2">
            <Label htmlFor="aibi-summary-brand">Brand</Label>
            <Select
              id="aibi-summary-brand"
              value={selectedBrandId ?? ''}
              onChange={(newBrandId: string) => {
                setSelectedBrandId(newBrandId);
                // The new brand's latest analysis may not have scanned the
                // platform that was selected; fall back to the endpoint's
                // default rather than asking for one this brand may lack.
                setSelectedProvider(undefined);
              }}
              options={brandOptions}
              disabled={brands.length < 2}
            />
          </div>
          <div className="yst-flex-1 yst-min-w-[200px] yst-space-y-2">
            <Label htmlFor="aibi-summary-provider">AI platform</Label>
            <Select
              id="aibi-summary-provider"
              // Until the user picks a platform, show the one the endpoint
              // actually returned metrics for.
              value={selectedProvider ?? summary?.aiProvider ?? ''}
              onChange={(newProvider: string) =>
                setSelectedProvider(newProvider as AIProviderEnum)
              }
              options={providerOptions}
              disabled={isLoadingMetrics || providers.length < 2}
            />
          </div>
        </div>

        {hasNoCompletedAnalysis ? (
          <div className="yst-flex yst-flex-col yst-items-center yst-gap-3 yst-rounded-lg yst-border yst-border-slate-200 yst-p-6 yst-text-center">
            {analysisDisabled ? (
              <>
                <p className="yst-text-slate-600">
                  Automatic analysis is disabled for this brand, so no insights
                  are available yet.
                </p>
                <Button variant="secondary" {...ctaProps('/settings')}>
                  Manage settings
                </Button>
              </>
            ) : setupIncomplete ? (
              <>
                <p className="yst-text-slate-600">
                  Finish setting up your brand to start your first analysis.
                </p>
                <Button
                  variant="secondary"
                  {...ctaProps(`/setup/${selectedBrandId}/queries`)}
                >
                  Finish setup
                  <ArrowRightIcon className="yst-ml-2 yst-h-4 yst-w-4" />
                </Button>
              </>
            ) : (
              <>
                <Spinner size="8" variant="primary" />
                <p className="yst-text-slate-600">
                  Your first analysis is being prepared. We'll email you when
                  your brand insights are ready.
                </p>
              </>
            )}
          </div>
        ) : (
          <MetricsGrid summary={summary ?? undefined} isLoading={isLoadingMetrics} />
        )}

        <div className="yst-flex yst-justify-end">
          <Button variant="primary" style={GRADIENT_STYLE} {...ctaProps('/')}>
            Visit AI Brand Insights
            <ArrowRightIcon className="yst-ml-2 yst-h-4 yst-w-4" />
          </Button>
        </div>
      </Card.Content>
    </Card>
  );
};

/**
 * AiBrandInsightsSummary — a compact, self-contained AI Brand Insights card
 * intended for embedding on a host application's homepage/dashboard.
 *
 * It configures its own data/auth (reusing the same token exchange as the full
 * AIInsightsClient) and wraps itself in the required providers, so it can be
 * dropped in anywhere without additional setup.
 */
const AiBrandInsightsSummary: FC<AiBrandInsightsSummaryProps> = ({
  token = '',
  apiUrl,
  isRtl = false,
  className,
  style,
  onNavigate,
  hrefFor,
  linkTarget,
}) => {
  // Configuration must be applied before any query fires; gate rendering of
  // the querying content until it is (mirrors AIInsightsClient in src/lib.tsx).
  const [configuring, setConfiguring] = useState(true);

  useEffect(() => {
    if (apiUrl) {
      setApiBaseUrl(apiUrl);
    }
    setMyYoastAccessTokenFactory(
      typeof token !== 'function' ? () => token : token,
    );
    // The QueryClient is a module-level singleton, so a change of token/apiUrl
    // (e.g. switching users) must drop cached data to avoid serving the
    // previous auth context's brands/analyses under the same query keys.
    queryClient.clear();
    setConfiguring(false);
  }, [apiUrl, token]);

  const wrapperClassName = ['ai-insights-app', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName} style={style}>
      <Root context={{ isRtl }}>
        {configuring ? (
          <SummarySkeleton
            onNavigate={onNavigate}
            hrefFor={hrefFor}
            linkTarget={linkTarget}
          />
        ) : (
          <QueryClientProvider client={queryClient}>
            <SummaryContent
              onNavigate={onNavigate}
              hrefFor={hrefFor}
              linkTarget={linkTarget}
            />
          </QueryClientProvider>
        )}
      </Root>
    </div>
  );
};

export default AiBrandInsightsSummary;
